import Vue from 'vue'

import ApiService from '@/core/services/api.service'
import JwtService from '@/core/services/jwt.service'

import { LOGOUT } from './auth.module.js'
import router from '@/router'

import { eventBus } from '@/main'

// action types
export const GET_RECIPES = 'getRecipes'
export const MANAGE_RECIPE = 'manageRecipe'
export const DELETE_RECIPE = 'deleteRecipe'
export const GET_RECIPE_VERSIONS = 'getRecipeVersions'
export const GET_RECIPE_VERSION = 'getRecipeVersion'
export const MANAGE_RECIPE_VERSION = 'manageRecipeVersion'
export const DELETE_RECIPE_VERSION = 'deleteRecipeVersion'
export const INSTALL_RECIPE_VERSION = 'installRecipeVersion'

// mutation types
export const SAVE_RECIPES = 'saveRecipes'
export const CLEAR_RECIPES = 'clearRecipes'
export const SAVE_RECIPE_VERSIONS = 'saveRecipeVersions'
export const CLEAR_RECIPE_VERSIONS = 'clearRecipeVersions'
export const SAVE_PENDING_RECIPE_VERSION_ID = 'savePendingRecipeVersionId'
export const CLEAR_PENDING_RECIPE_VERSION_ID = 'clearPendingRecipeVersionId'
export const SELECT_RECIPE = 'selectRecipe'
export const UNSELECT_RECIPE = 'unselectRecipe'
export const SAVE_MANAGE_RECIPE_ERRORS = 'saveManageRecipeErrors'
export const CLEAR_MANAGE_RECIPE_ERRORS = 'clearManageRecipeErrors'
export const SAVE_MANAGE_RECIPE_VERSION_ERRORS = 'saveManageRecipeVersionErrors'
export const CLEAR_MANAGE_RECIPE_VERSION_ERRORS = 'clearManageRecipeVersionErrors'
export const SAVE_RECIPE_VERSION = 'saveRecipeVersion'
export const CLEAR_RECIPE_VERSION = 'clearRecipeVersion'
export const SAVE_INSTALL_RECIPE_RESPONSE = 'saveInstallRecipeResponse'
export const CLEAR_INSTALL_RECIPE_RESPONSE = 'clearInstallRecipeResponse'

const SET_RESPONSE_STATE = 'setResponseState'
const TOAST_RECIPES = 'showToast/Recipes'
const TOAST_RECIPE_INSTALLATION = 'showToast/RecipeInstallation'
export const DEFAULT_SELECTED_RECIPE = {
  name: '',
  slug: '',
  tags: [],
  description: '',
  vcs_src_url: '',
  version: '',
  source: '',
}
export const DEFAULT_SELECTED_RECIPE_VERSION = {
  version: '',
  source: '',
  default: true,
}

// returns recipe based on _id
function findRecipe(state, id) {
  return state.recipesList.filter(item => item._id === id)[0]
}

// Back-end GET/POST/PUT requests required and returned data in format that I was not comfortable to wotk with on front-end, so I've managed to transform it both at both receiving and sending
function convertForBackend(context) {
  const income = { ...context.state.selectedRecipe }

  // convert .tags Array => String
  if (income.tags) {
    income.tags = context.state.selectedRecipe.tags.join(' ')
  }

  // delete front-end additional params
  delete income._showDetails
  delete income.versions

  return income
}

function convertForFrontend(state, recipes) {
  const income = recipes
  if (income.length > 0) {
    for (let i = 0; i < income.length; i++) {
      // add _showDetails param for each element in recipes
      // because we want possibility to expand rows
      // also keep .versions if user already opened them
      // see => https://bootstrap-vue.org/docs/components/table#row-details-support
      const existingRecipe = findRecipe(state, income[i]._id)
      if (!existingRecipe) {
        income[i]._showDetails = false
        income[i].versions = []
      } else {
        income[i]._showDetails = existingRecipe._showDetails
        if (
          existingRecipe.versions &&
          existingRecipe.versions.length > 0) {
          income[i].versions = existingRecipe.versions
        }
      }

      // convert .tags String => Array
      // because this format requires <b-form-tags>
      // see => https://bootstrap-vue.org/docs/components/form-tags#tag-creation-using-separators
      if (income[i].tags.length > 0) {
        income[i].tags = income[i].tags.split(' ')
      } else {
        income[i].tags = [] // empty array by default
      }
    }
  }

  return income
}

// @link https://stackoverflow.com/questions/7467840/nl2br-equivalent-in-javascript
/* eslint-disable-next-line no-unused-vars */
function nl2br(str, is_xhtml) {
  if (typeof str === 'undefined' || str === null) {
      return ''
  }
  var breakTag = (is_xhtml || typeof is_xhtml === 'undefined') ? '<br />' : '<br>'
  return (str + '').replace(/([^>\r\n]?)(\r\n|\n\r|\r|\n)/g, '$1' + breakTag + '$2')
}

const state = {
  recipesList: [],
  selectedRecipe: { ...DEFAULT_SELECTED_RECIPE },
  selectedRecipeId: null,
  pendingRecipeVerions: [],
  manageRecipeErrors: [],
  manageRecipeVersionErrors: [],
  selectedRecipeVersion: { ...DEFAULT_SELECTED_RECIPE_VERSION },
  selectedRecipeVersionId: null,
  installRecipeResponse: [],
  sourceCodePlaceholder: '#!/bin/bash\n\necho "Hello world"',

  responses: {
    recipesList: false,
    manageRecipe: false,
    deleteRecipe: false,
    recipeVersions: false,
    recipeVersion: false,
    manageRecipeVersion: false,
    deleteRecipeVersion: false,
    installRecipeVersion: false,
  },
}

const getters = {
  recipesList(state) {
    return state.recipesList
  },
  uninstalledRecipesList(state, getters, rootGetters) {
    const allRecipes = state.recipesList
    const recipeGroupRecipes = rootGetters.recipesGroups.selectedRecipeGroup.recipes_list

    return allRecipes.filter(obj_all =>
      !recipeGroupRecipes.some(obj_installed => obj_installed._id === obj_all._id)
    )
  },
  uninstalledRecipesListEmpty(state, getters) {
    return getters.uninstalledRecipesList.length == 0
  },
  recipesEmpty(state) {
    return state.recipesList.length == 0
  },
  totalRecipes(state) {
    return state.recipesList.length
  },
  recipeVersionsEmpty: state => id => {
    const target = findRecipe(state, id)
    if (target) {
      return target.versions && Array.isArray(target.versions) && target.versions.length == 0
    }
  },
  defaultCheckboxDisabled: state => id => {
    // This function returns TRUE if:
    // 1) This is brand new script version and array of versions is empty
    // 2) This script is only one in the list and this field is editing rn
    // see => https://youtrack.musqogee.com/issue/HT-1378
    const target = findRecipe(state, id)
    if (target) {
      if (target.versions) {
        return (state.selectedRecipeVersionId == null && target.versions.length == 0)
        || (state.selectedRecipeVersionId && target.versions.length == 1)
      } else {
        // if .versions [] don't exist that means recipe has 0 script versions
        return true
      }
    }
  },
  recipeTagsEmpty: state => id => {
    const target = findRecipe(state, id)
    return target.tags.length == 0
  },
  recipeVersionsPending: state => id => {
    return state.pendingRecipeVerions.some(i => i == id)
  },
  recipeVersions: state => id => {
    const target = findRecipe(state, id)
    return target ? target.versions : []
  },
  selectedRecipe(state) {
    return state.selectedRecipe
  },
  selectedRecipeId(state) {
    return state.selectedRecipeId
  },
  selectedRecipeExist(state) {
    return state.recipesList.some(recipe => recipe._id == state.selectedRecipeId)
  },
  selectedRecipeVersion(state) {
    return state.selectedRecipeVersion
  },
  selectedRecipeVersionId(state) {
    return state.selectedRecipeVersionId
  },
  manageRecipeModalTitle(state) {
    return state.selectedRecipeId ? 'Edit Recipe' : 'Add New Recipe'
  },
  manageRecipeVersionModalTitle(state) {
    return state.selectedRecipeVersionId ? 'Edit Script Version' : 'Add New Script Version'
  },
  selectedRecipeVersionExist(state) {
    return state.selectedRecipeVersionId !== null && state.selectedRecipeVersionId !== undefined
  },
  beautifiedVersionTitle: () => version => {
    const def = version.default == 'on' ? ', default' : ''
    return `v${version.version} [${version.size_unit}${def}]`
  },
  openedRecipeVersions(state) {
    const allVersions = []
    if (state.recipesList.length > 0) {
      for (let i = 0; i < state.recipesList.length; i++) {
        if (
          state.recipesList[i].versions &&
          state.recipesList[i].versions.length > 0) {
          for (let n = 0; n < state.recipesList[i].versions.length; n++) {
            allVersions.push(state.recipesList[i].versions[n])
          }
        }
      }
    }
    return allVersions
  },
  manageRecipeErrors(state) {
    return state.manageRecipeErrors
  },
  manageRecipeError: state => field => {
    return state.manageRecipeErrors.filter(err => err.field == field)
  },
  manageRecipeVersionErrors(state) {
    return state.manageRecipeVersionErrors
  },
  manageRecipeVersionError: state => field => {
    return state.manageRecipeVersionErrors.filter(err => err.field == field)
  },
  recipeGroupsEmpty: state => id => {
    const target = findRecipe(state, id)
    return target.groups.length == 0
  },
  recipeHasDescription: state => id => {
    const target = findRecipe(state, id)
    return target.description && target.description.length > 0
  },
  installRecipeResponse(state) {
    const income = state.installRecipeResponse
    let formatedResponse = ''

    if (income.length > 0) {
      for (let i = 0; i < income.length; i++) {
        const res = income[i]

        formatedResponse += res.success === true ? '<span class="text-success font-weight-bolder">SUCCESS ✔️</span>' : '<span class="text-danger font-weight-bolder">FAILED ❌</span>'
        formatedResponse += '<br>' + res.output
        formatedResponse += i < income.length - 1 ? '<br><br>------------------------------------------<br><br>' : ''
      }
    } else {
      formatedResponse = '<span class="text-danger font-weight-bolder">FAILED ❌</span>'
    }

    return formatedResponse
  },
  sourceCodePlaceholder(state) {
    return state.sourceCodePlaceholder
  },
  recipesResponses: state => type => {
    return state.responses[type]
  }
}

const actions = {
  [GET_RECIPES](context) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'recipesList', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        ApiService.get(process.env.VUE_APP_RECIPES)
          .then(({ data }) => {
            context.commit(SAVE_RECIPES, data)
            resolve(data)
          })
          .catch(err => {
            eventBus.$emit(TOAST_RECIPES, {
              message: err.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'recipesList', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [MANAGE_RECIPE](context) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'manageRecipe', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()

        const payload = convertForBackend(context)
        let method
        let url

        if (context.getters.selectedRecipeExist) {
          method = 'put'
          url = `${process.env.VUE_APP_RECIPE}/${state.selectedRecipeId}`
        } else {
          method = 'post'
          url = process.env.VUE_APP_RECIPE
        }

        ApiService[method](url, payload)
          .then(({ data }) => {
            context.dispatch(GET_RECIPES)
            eventBus.$emit(TOAST_RECIPES, {
              message: context.getters.selectedRecipeExist ? 'Recipe updated' : 'Added new recipe',
              variant: 'success'
            })
            resolve(data)
          })
          .catch(err  => {
            const error = err.response.data
            context.commit(SAVE_MANAGE_RECIPE_ERRORS, error.errors)
            eventBus.$emit(TOAST_RECIPES, {
              message: error.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'manageRecipe', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [DELETE_RECIPE](context) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'deleteRecipe', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        ApiService.delete(`${process.env.VUE_APP_RECIPE}/${state.selectedRecipeId}`)
          .then(({ data }) => {
            context.dispatch(GET_RECIPES)
            eventBus.$emit(TOAST_RECIPES, {
              message: data.message,
              variant: 'success'
            })
            resolve(data)
          })
          .catch(err => {
            const errors = err.response.data.errors
            for (let e = 0; e < errors.length; e++) {
              eventBus.$emit(TOAST_RECIPES, {
                message: errors[e].message,
                variant: 'danger'
              })
            }
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'deleteRecipe', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [GET_RECIPE_VERSIONS](context, { id, isShowing }) {
    if (isShowing) {
      context.commit(CLEAR_RECIPE_VERSIONS, id)
    } else {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'recipeVersions', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        context.commit(SAVE_PENDING_RECIPE_VERSION_ID, id)
        ApiService.get(`${process.env.VUE_APP_RECIPE}/${id}/versions`)
          .then(({ data }) => {
            context.commit(SAVE_RECIPE_VERSIONS, {
              versions: data,
              id: id
            })
          })
          .catch(err => {
            eventBus.$emit(TOAST_RECIPES, {
              message: err.message,
              variant: 'danger'
            })
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'recipeVersions', status: false })
            eventBus.$emit('endProgress')
            context.commit(CLEAR_PENDING_RECIPE_VERSION_ID, id)
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    }
  },
  [GET_RECIPE_VERSION](context, id) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'recipeVersion', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        ApiService.get(`${process.env.VUE_APP_RECIPE}/version/${id}`)
          .then(({ data }) => {
            context.commit(SAVE_RECIPE_VERSION, data)
            resolve(data)
          })
          .catch(err => {
            eventBus.$emit(TOAST_RECIPES, {
              message: err.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'recipeVersion', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [MANAGE_RECIPE_VERSION](context) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'manageRecipeVersion', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()

        let method
        let url

        if (context.getters.selectedRecipeVersionExist) {
          method = 'put'
          url = `${process.env.VUE_APP_RECIPE}/version/${context.state.selectedRecipeVersionId}`
        } else {
          method = 'post'
          url = `${process.env.VUE_APP_RECIPE}/${context.state.selectedRecipeId}/version`
        }

        ApiService[method](url, context.state.selectedRecipeVersion)
          .then(({ data }) => {
            context.dispatch(GET_RECIPES)
            context.dispatch(GET_RECIPE_VERSIONS, {
              id: context.state.selectedRecipe._id,
              isShowing: false
            })
            eventBus.$emit(TOAST_RECIPES, {
              message: context.getters.selectedRecipeVersionExist ? 'Script version updated' : 'Added new script version',
              variant: 'success'
            })
            resolve(data)
          })
          .catch(err  => {
            const error = err.response.data
            context.commit(SAVE_MANAGE_RECIPE_VERSION_ERRORS, error.errors)
            eventBus.$emit(TOAST_RECIPES, {
              message: error.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'manageRecipeVersion', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [DELETE_RECIPE_VERSION](context) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'deleteRecipeVersion', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        ApiService.delete(`${process.env.VUE_APP_RECIPE}/version/${state.selectedRecipeVersionId}`)
          .then(({ data }) => {
            context.dispatch(GET_RECIPES)
            context.dispatch(GET_RECIPE_VERSIONS, {
              id: context.state.selectedRecipe._id,
              isShowing: false
            })
            eventBus.$emit(TOAST_RECIPES, {
              message: data.message,
              variant: 'success'
            })
            resolve(data)
          })
          .catch(err => {
            const errors = err.response.data.errors
            for (let e = 0; e < errors.length; e++) {
              eventBus.$emit(TOAST_RECIPES, {
                message: errors[e].message,
                variant: 'danger'
              })
            }
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'deleteRecipeVersion', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [INSTALL_RECIPE_VERSION](context, { version, server }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'installRecipeVersion', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        ApiService.post(
          `${process.env.VUE_APP_RECIPE}/version/${version}/run`,
          { server: server }
          )
          .then(({ data }) => {
            context.commit(SAVE_INSTALL_RECIPE_RESPONSE, data)
            resolve(data)
          })
          .catch(err => {
            if (err.errors && err.errors.length > 0) {
              for (let e = 0; e < err.errors.length; e++) {
                eventBus.$emit(TOAST_RECIPE_INSTALLATION, {
                  message: err.errors[e].message,
                  variant: 'danger'
                })
              }
            } else {
              eventBus.$emit(TOAST_RECIPE_INSTALLATION, {
                message: err.message,
                variant: 'danger'
              })
            }
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'installRecipeVersion', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
}

const mutations = {
  [SAVE_RECIPES](state, recipes) {
    state.recipesList = convertForFrontend(state, recipes)
  },
  [CLEAR_RECIPES](state) {
    state.recipesList = []
  },
  [SAVE_RECIPE_VERSIONS](state, { versions, id }) {
    const target = findRecipe(state, id)
    Vue.set(target, 'versions', versions)
    Vue.set(state.selectedRecipe, 'versions', versions)
  },
  [CLEAR_RECIPE_VERSIONS](state, id) {
    const target = findRecipe(state, id)
    Vue.set(target, 'versions', [])
    Vue.set(state.selectedRecipe, 'versions', [])
  },
  [SAVE_PENDING_RECIPE_VERSION_ID](state, id) {
    if (!state.pendingRecipeVerions.some(i => i == id)) {
      state.pendingRecipeVerions.push(id)
    }
  },
  [CLEAR_PENDING_RECIPE_VERSION_ID](state, id) {
    const index = state.pendingRecipeVerions.indexOf(id)
    if (index !== -1) {
      state.pendingRecipeVerions.splice(index, 1)
    }
  },
  [SELECT_RECIPE](state, recipe) {
    Object.assign(state.selectedRecipe, recipe)
    state.selectedRecipeId = recipe._id
  },
  [UNSELECT_RECIPE](state) {
    state.selectedRecipe = {}
    Object.assign(state.selectedRecipe, DEFAULT_SELECTED_RECIPE)
    state.selectedRecipeId = null
  },
  [SAVE_MANAGE_RECIPE_ERRORS](state, errors) {
    state.manageRecipeErrors = errors
  },
  [CLEAR_MANAGE_RECIPE_ERRORS](state) {
    state.manageRecipeErrors = []
  },
  [SAVE_MANAGE_RECIPE_VERSION_ERRORS](state, errors) {
    state.manageRecipeVersionErrors = errors
  },
  [CLEAR_MANAGE_RECIPE_VERSION_ERRORS](state) {
    state.manageRecipeVersionErrors = []
  },
  [SAVE_RECIPE_VERSION](state, version) {
    Object.assign(state.selectedRecipeVersion, version)
    state.selectedRecipeVersionId = version._id
  },
  [CLEAR_RECIPE_VERSION](state) {
    state.selectedRecipeVersion = {}
    Object.assign(state.selectedRecipeVersion, DEFAULT_SELECTED_RECIPE_VERSION)
    state.selectedRecipeVersionId = null
  },
  [SAVE_INSTALL_RECIPE_RESPONSE](state, response) {
    state.installRecipeResponse = response
  },
  [CLEAR_INSTALL_RECIPE_RESPONSE](state) {
    state.installRecipeResponse = []
  },
  [SET_RESPONSE_STATE](state, { type, status }) {
    state.responses[type] = status
  },
}

export default {
  state,
  actions,
  mutations,
  getters,
}
